
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function CloudServicesPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [selectedIndustry, setSelectedIndustry] = useState(isNL ? 'Selecteer uw branche' : 'Select your industry');
  const [selectedTimeline, setSelectedTimeline] = useState(isNL ? 'Selecteer tijdlijn' : 'Select timeline');
  const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
  const [showTimelineDropdown, setShowTimelineDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToConsultation = () => {
    document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    document.getElementById('cloud-services')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleIndustrySelect = (value: string, label: string) => {
    setSelectedIndustry(label);
    (document.getElementById('industry') as HTMLInputElement).value = value;
    setShowIndustryDropdown(false);
  };

  const handleTimelineSelect = (value: string, label: string) => {
    setSelectedTimeline(label);
    (document.getElementById('timeline') as HTMLInputElement).value = value;
    setShowTimelineDropdown(false);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 500) {
      setCharCount(text.length);
    } else {
      e.target.value = text.substring(0, 500);
      setCharCount(500);
    }
  };

  const formUrl = isNL
    ? 'https://readdy.ai/api/form/d6dgnh6qd308q1a0a210'
    : 'https://readdy.ai/api/form/d6dgnh6qd308q1a0a210';

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;

    const checkboxes = form.querySelectorAll('input[name="cloud_services"]:checked');
    if (checkboxes.length === 0) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new URLSearchParams();
      const fields = ['company_name', 'industry', 'contact_name', 'job_title', 'email', 'phone', 'current_infrastructure', 'timeline', 'message', 'privacy_policy'];
      fields.forEach((field) => {
        const el = form.elements.namedItem(field) as HTMLInputElement | null;
        if (el) formData.append(field, el.value);
      });

      const checkedServices: string[] = [];
      checkboxes.forEach((cb) => checkedServices.push((cb as HTMLInputElement).value));
      formData.append('cloud_services', checkedServices.join(', '));

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
        setSelectedIndustry(isNL ? 'Selecteer uw branche' : 'Select your industry');
        setSelectedTimeline(isNL ? 'Selecteer tijdlijn' : 'Select timeline');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const industryOptions = [
    { value: 'technology', label: isNL ? 'Technologie' : 'Technology' },
    { value: 'finance', label: isNL ? 'Financiën & Bankwezen' : 'Finance & Banking' },
    { value: 'healthcare', label: isNL ? 'Gezondheidszorg' : 'Healthcare' },
    { value: 'manufacturing', label: isNL ? 'Productie' : 'Manufacturing' },
    { value: 'retail', label: isNL ? 'Retail & E-commerce' : 'Retail & E-commerce' },
    { value: 'education', label: isNL ? 'Onderwijs' : 'Education' },
    { value: 'other', label: isNL ? 'Overig' : 'Other' },
  ];

  const timelineOptions = [
    { value: 'immediate', label: isNL ? 'Direct (Binnen 1 maand)' : 'Immediate (Within 1 month)' },
    { value: 'short', label: isNL ? 'Korte termijn (1-3 maanden)' : 'Short-term (1-3 months)' },
    { value: 'medium', label: isNL ? 'Middellange termijn (3-6 maanden)' : 'Medium-term (3-6 months)' },
    { value: 'long', label: isNL ? 'Lange termijn (6+ maanden)' : 'Long-term (6+ months)' },
    { value: 'planning', label: isNL ? 'Alleen oriënterend' : 'Just planning' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={`${prefix}/`} className="text-gray-500 hover:text-primary">Home</a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href={`${prefix}/`} className="text-gray-500 hover:text-primary">
              {isNL ? 'IT-diensten' : 'IT Services'}
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">
              {isNL ? 'Clouddiensten' : 'Cloud Services'}
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full" style={{
        backgroundImage: 'url(https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/c0e0e8e0e8e0e8e0e8e0e8e0e8e0e8e0.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="w-full max-w-2xl">
            <span className="text-primary font-semibold mb-4 block">
              {isNL ? 'ZAKELIJKE CLOUDOPLOSSINGEN' : 'ENTERPRISE CLOUD SOLUTIONS'}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {isNL ? 'Clouddiensten & Oplossingen' : 'Cloud Services & Solutions'}
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              {isNL
                ? 'Migreer, beheer en optimaliseer uw cloudomgeving met de uitgebreide clouddiensten van Thyamis. Van strategie tot implementatie, wij leveren veilige, schaalbare en kosteneffectieve cloudoplossingen.'
                : 'Migrate, manage, and optimize your cloud environment with Thyamis’s comprehensive cloud services. From strategy to implementation, we deliver secure, scalable, and cost-effective cloud solutions.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToConsultation}
                className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-calendar-line"></i>
                </div>
                {isNL ? 'Cloudconsultatie Plannen' : 'Schedule Cloud Consultation'}
              </button>
              <button
                onClick={scrollToServices}
                className="bg-transparent border border-gray-300 text-gray-700 px-6 py-3 rounded-lg whitespace-nowrap font-medium inline-flex items-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-eye-line"></i>
                </div>
                {isNL ? 'Bekijk Diensten' : 'View Services'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Cloud Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Uitgebreide Clouddiensten' : 'Comprehensive Cloud Services'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Van cloudmigratie tot doorlopend beheer, ons expertteam levert end-to-end cloudoplossingen op maat van uw bedrijfsbehoeften.'
                : 'From cloud migration to ongoing management, our expert team delivers end-to-end cloud solutions tailored to your business needs.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" id="cloud-services">
            {/* Cloud Migration Services */}
            <div className="bg-white rounded-lg shadow-md border border-gray-100 p-6 transition-all hover:shadow-lg hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-cloud-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Cloudmigratiediensten' : 'Cloud Migration Services'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Migreer uw bestaande infrastructuur naadloos naar de cloud met minimale downtime en maximale efficiëntie.'
                  : 'Seamlessly migrate your existing infrastructure to the cloud with minimal downtime and maximum efficiency.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Beoordeling & Planning' : 'Assessment & Planning'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Datamigratie' : 'Data Migration'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-primary mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Applicatiemodernisering' : 'Application Modernization'}
                  </span>
                </li>
              </ul>
              <a href="#" className="text-primary font-medium flex items-center cursor-pointer">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Hybrid Cloud Solutions */}
            <div className="bg-white rounded-lg shadow-md border border-gray-100 p-6 transition-all hover:shadow-lg hover:-translate-y-2">
              <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                  <i className="ri-server-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Hybride Cloudoplossingen' : 'Hybrid Cloud Solutions'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Combineer on-premises infrastructuur met clouddiensten voor optimale flexibiliteit en controle over uw IT-omgeving.'
                  : 'Combine on-premises infrastructure with cloud services for optimal flexibility and control over your IT environment.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Hybride Architectuurontwerp' : 'Hybrid Architecture Design'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Werkbelastingoptimalisatie' : 'Workload Optimization'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-purple-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Naadloze Integratie' : 'Seamless Integration'}
                  </span>
                </li>
              </ul>
              <a href="#" className="text-purple-600 font-medium flex items-center cursor-pointer">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Multi-Cloud Management */}
            <div className="bg-white rounded-lg shadow-md border border-gray-100 p-6 transition-all hover:shadow-lg hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-settings-3-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Multi-Cloud Beheer' : 'Multi-Cloud Management'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Beheer en optimaliseer werkbelastingen over meerdere cloudproviders met uniforme monitoring en governance.'
                  : 'Manage and optimize workloads across multiple cloud providers with unified monitoring and governance.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Gecentraliseerd Beheer' : 'Centralized Management'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Kostenoptimalisatie' : 'Cost Optimization'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-green-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Prestatiemonitoring' : 'Performance Monitoring'}
                  </span>
                </li>
              </ul>
              <a href="#" className="text-green-600 font-medium flex items-center cursor-pointer">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>

            {/* Cloud Security Services */}
            <div className="bg-white rounded-lg shadow-md border border-gray-100 p-6 transition-all hover:shadow-lg hover:-translate-y-2">
              <div className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-red-600">
                  <i className="ri-shield-check-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {isNL ? 'Cloudbeveiligingsdiensten' : 'Cloud Security Services'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Bescherm uw cloudinfrastructuur met uitgebreide beveiligingsmaatregelen en nalevingskaders.'
                  : 'Protect your cloud infrastructure with comprehensive security measures and compliance frameworks.'}
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-red-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Beveiligingsbeoordeling' : 'Security Assessment'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-red-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? 'Nalevingsbeheer' : 'Compliance Management'}
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-4 h-4 flex items-center justify-center text-red-600 mt-1 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-sm text-gray-700">
                    {isNL ? '24/7 Monitoring' : '24/7 Monitoring'}
                  </span>
                </li>
              </ul>
              <a href="#" className="text-red-600 font-medium flex items-center cursor-pointer">
                {isNL ? 'Meer Informatie' : 'Learn More'}
                <div className="w-4 h-4 flex items-center justify-center ml-1">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Thyamis Cloud Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Waarom Kiezen voor Thyamis Clouddiensten' : 'Why Choose Thyamis Cloud Services'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Ervaar de voordelen van zakelijke cloudoplossingen ontworpen om uw bedrijfsgroei te versnellen.'
                : 'Experience the advantages of enterprise-grade cloud solutions designed to accelerate your business growth.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-10 h-10 flex items-center justify-center text-primary">
                  <i className="ri-money-dollar-circle-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">
                {isNL ? 'Kostenoptimalisatie' : 'Cost Optimization'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Verlaag infrastructuurkosten met tot 40% dankzij onze geoptimaliseerde cloudoplossingen en right-sizing strategieën.'
                  : 'Reduce infrastructure costs by up to 40% with our optimized cloud solutions and right-sizing strategies.'}
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-10 h-10 flex items-center justify-center text-green-600">
                  <i className="ri-expand-diagonal-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">
                {isNL ? 'Schaalbaarheid & Flexibiliteit' : 'Scalability & Flexibility'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Schaal resources direct op of af op basis van vraag, voor optimale prestaties en kostenefficiëntie.'
                  : 'Scale resources up or down instantly based on demand, ensuring optimal performance and cost efficiency.'}
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-10 h-10 flex items-center justify-center text-red-600">
                  <i className="ri-shield-star-line ri-2x"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-4">
                {isNL ? 'Verbeterde Beveiliging' : 'Enhanced Security'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Profiteer van zakelijke beveiligingsmaatregelen en nalevingskaders om uw kritieke gegevens te beschermen.'
                  : 'Benefit from enterprise-grade security measures and compliance frameworks to protect your critical data.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Cloud Partners */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {isNL ? 'Onze Cloudpartners' : 'Our Cloud Partners'}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {isNL
                ? 'Wij werken samen met toonaangevende cloudproviders om de beste oplossingen voor uw bedrijf te leveren.'
                : 'We partner with leading cloud providers to deliver the best solutions for your business.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Amazon Web Services */}
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-32 h-32 mx-auto mb-6 rounded-lg overflow-hidden bg-white p-4 shadow-sm">
                <img
                  src="https://public.readdy.ai/ai/img_res/a566a96c17554bab8a3003a8b4ff131f.jpg"
                  alt="Amazon Web Services Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3">Amazon Web Services</h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Geavanceerde AWS-partner die uitgebreide cloudoplossingen levert met zakelijke beveiliging en schaalbaarheid.'
                  : 'Advanced AWS partner providing comprehensive cloud solutions with enterprise-grade security and scalability.'}
              </p>
              <div className="flex justify-center space-x-2">
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">EC2</span>
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">S3</span>
                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs">RDS</span>
              </div>
            </div>

            {/* Microsoft Azure */}
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-32 h-32 mx-auto mb-6 rounded-lg overflow-hidden bg-white p-4 shadow-sm">
                <img
                  src="https://readdy.ai/api/search-image?query=Microsoft%20Azure%20official%20logo%20on%20pristine%20white%20background%2C%20professional%20corporate%20identity%2C%20blue%20gradient%20color%20scheme%2C%20modern%20cloud%20platform%20technology%20branding%2C%20high%20quality%20vector%20style%20illustration%2C%20clean%20minimalist%20design%2C%20centered%20layout%2C%20crisp%20sharp%20details%2C%20enterprise%20software%20aesthetic&width=400&height=400&seq=azure-logo-cloud-services-002&orientation=squarish"
                  alt="Microsoft Azure Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3">Microsoft Azure</h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Gold-partner gespecialiseerd in Azure-clouddiensten, hybride oplossingen en Microsoft 365-integraties.'
                  : 'Gold partner specializing in Azure cloud services, hybrid solutions, and Microsoft 365 integrations.'}
              </p>
              <div className="flex justify-center space-x-2">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">Azure VM</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">SQL DB</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">AD</span>
              </div>
            </div>

            {/* Google Cloud Platform */}
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="w-32 h-32 mx-auto mb-6 rounded-lg overflow-hidden bg-white p-4 shadow-sm">
                <img
                  src="https://readdy.ai/api/search-image?query=Google%20Cloud%20Platform%20GCP%20official%20logo%20on%20clean%20white%20background%2C%20professional%20corporate%20branding%2C%20multicolor%20red%20yellow%20blue%20green%20scheme%2C%20modern%20cloud%20infrastructure%20technology%20identity%2C%20high%20quality%20vector%20style%20illustration%2C%20minimalist%20clean%20design%2C%20centered%20composition%2C%20sharp%20crisp%20details%2C%20enterprise%20tech%20aesthetic&width=400&height=400&seq=gcp-logo-cloud-services-003&orientation=squarish"
                  alt="Google Cloud Platform Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold mb-3">Google Cloud Platform</h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Premier-partner die GCP-oplossingen levert met focus op AI/ML, data-analyse en moderne applicatieontwikkeling.'
                  : 'Premier partner delivering GCP solutions with focus on AI/ML, data analytics, and modern application development.'}
              </p>
              <div className="flex justify-center space-x-2">
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">Compute</span>
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">BigQuery</span>
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">AI/ML</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Your Cloud Consultation */}
      <section className="py-16 bg-gray-50" id="consultation-form">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                {isNL ? 'Plan Uw Cloudconsultatie' : 'Schedule Your Cloud Consultation'}
              </h2>
              <p className="text-gray-600">
                {isNL
                  ? 'Ontvang deskundig advies over uw cloudstrategie. Onze specialisten beoordelen uw behoeften en adviseren de beste cloudoplossingen voor uw bedrijf.'
                  : 'Get expert advice on your cloud strategy. Our specialists will assess your needs and recommend the best cloud solutions for your business.'}
              </p>
            </div>

            <form
              id={isNL ? 'cloud-consultation-form-nl' : 'cloud-consultation-form'}
              data-readdy-form
              onSubmit={handleFormSubmit}
              className="bg-white rounded-lg shadow-md p-8 space-y-6"
            >
              {/* Company Name & Industry */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Bedrijfsnaam *' : 'Company Name *'}
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw bedrijfsnaam in' : 'Enter your company name'}
                  />
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Branche *' : 'Industry *'}
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowIndustryDropdown(!showIndustryDropdown)}
                      className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm text-left bg-white flex items-center justify-between cursor-pointer"
                    >
                      <span>{selectedIndustry}</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </button>
                    {showIndustryDropdown && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-b shadow-lg z-10">
                        <div className="py-1">
                          {industryOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleIndustrySelect(opt.value, opt.label)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm cursor-pointer"
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input type="hidden" id="industry" name="industry" required />
                </div>
              </div>

              {/* Contact Name & Job Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Contactpersoon *' : 'Contact Name *'}
                  </label>
                  <input
                    type="text"
                    id="contact_name"
                    name="contact_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw volledige naam in' : 'Enter your full name'}
                  />
                </div>

                <div>
                  <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Functietitel *' : 'Job Title *'}
                  </label>
                  <input
                    type="text"
                    id="job_title"
                    name="job_title"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw functietitel in' : 'Enter your job title'}
                  />
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Zakelijk E-mailadres *' : 'Business Email *'}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw zakelijk e-mailadres in' : 'Enter your business email'}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    {isNL ? 'Telefoonnummer *' : 'Phone Number *'}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    placeholder={isNL ? 'Voer uw telefoonnummer in' : 'Enter your phone number'}
                  />
                </div>
              </div>

              {/* Current Infrastructure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {isNL ? 'Huidige Infrastructuur *' : 'Current Infrastructure *'}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="current_infrastructure"
                      value="on-premises"
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                      required
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Volledig On-Premises' : 'Fully On-Premises'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="current_infrastructure"
                      value="hybrid"
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Hybride (On-Premises + Cloud)' : 'Hybrid (On-Premises + Cloud)'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="current_infrastructure"
                      value="cloud"
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Volledig Cloudgebaseerd' : 'Fully Cloud-Based'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="current_infrastructure"
                      value="legacy"
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Verouderde Systemen' : 'Legacy Systems'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Cloud Services Needed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {isNL ? 'Gewenste Clouddiensten *' : 'Cloud Services Needed *'}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="cloud_services"
                      value="migration"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Cloudmigratie' : 'Cloud Migration'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="cloud_services"
                      value="hybrid"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Hybride Cloudoplossingen' : 'Hybrid Cloud Solutions'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="cloud_services"
                      value="multi-cloud"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Multi-Cloud Beheer' : 'Multi-Cloud Management'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="cloud_services"
                      value="security"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Cloudbeveiliging' : 'Cloud Security'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="cloud_services"
                      value="backup"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Back-up & Noodherstel' : 'Backup & Disaster Recovery'}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="cloud_services"
                      value="optimization"
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {isNL ? 'Kostenoptimalisatie' : 'Cost Optimization'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Project Timeline */}
              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                  {isNL ? 'Projecttijdlijn' : 'Project Timeline'}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTimelineDropdown(!showTimelineDropdown)}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm text-left bg-white flex items-center justify-between cursor-pointer"
                  >
                    <span>{selectedTimeline}</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </button>
                  {showTimelineDropdown && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-b shadow-lg z-10">
                      <div className="py-1">
                        {timelineOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleTimelineSelect(opt.value, opt.label)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm cursor-pointer"
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <input type="hidden" id="timeline" name="timeline" />
              </div>

              {/* Additional Requirements */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  {isNL ? 'Aanvullende Vereisten' : 'Additional Requirements'}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  onChange={handleMessageChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                  placeholder={isNL ? 'Vertel ons over uw specifieke cloudvereisten, uitdagingen of doelen' : 'Tell us about your specific cloud requirements, challenges, or goals'}
                ></textarea>
                <div className="mt-1 text-sm text-gray-500 flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-information-line"></i>
                  </div>
                  <span>{charCount}/500 {isNL ? 'tekens' : 'characters'}</span>
                </div>
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacy_policy"
                  name="privacy_policy"
                  required
                  className="w-4 h-4 mt-1 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="privacy_policy" className="ml-2 text-sm text-gray-600">
                  {isNL ? (
                    <>
                      Ik ga akkoord met de verwerking van mijn gegevens volgens het{' '}
                      <a href={`${prefix}/privacy-policy`} className="text-primary hover:underline">
                        Privacybeleid
                      </a>{' '}
                      *
                    </>
                  ) : (
                    <>
                      I agree to the processing of my data according to the{' '}
                      <a href={`${prefix}/privacy-policy`} className="text-primary hover:underline">
                        Privacy Policy
                      </a>{' '}
                      *
                    </>
                  )}
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary text-white px-8 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors flex items-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-4 h-4 flex items-center justify-center mr-2">
                    <i className="ri-send-plane-line"></i>
                  </div>
                  {isSubmitting
                    ? isNL ? 'Verzenden...' : 'Submitting...'
                    : isNL ? 'Consultatie Plannen' : 'Schedule Consultation'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center">
                  {isNL
                    ? 'Bedankt voor uw consultatieaanvraag! Onze cloudspecialisten nemen binnen 24 uur contact met u op.'
                    : 'Thank you for your consultation request! Our cloud specialists will contact you within 24 hours.'}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center">
                  {isNL
                    ? 'Sorry, er is een fout opgetreden. Selecteer minimaal één clouddienst en probeer het opnieuw.'
                    : 'Sorry, there was an error. Please select at least one cloud service and try again.'}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative" style={{
        backgroundImage: 'url(https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/c0e0e8e0e8e0e8e0e8e0e8e0e8e0e8e4.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-primary/90"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              {isNL ? 'Klaar om Uw Cloudreis te Versnellen?' : 'Ready to Accelerate Your Cloud Journey?'}
            </h2>
            <p className="text-blue-100 mb-8">
              {isNL
                ? 'Sluit u aan bij honderden bedrijven die hun activiteiten hebben getransformeerd met Thyamis cloudoplossingen. Onze experts staan klaar om een cloudstrategie op maat te ontwerpen die uw bedrijf vooruit helpt.'
                : 'Join hundreds of businesses that have transformed their operations with Thyamis cloud solutions. Our experts are ready to design a custom cloud strategy that drives your business forward.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={scrollToConsultation}
                className="bg-white text-primary px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-100 transition-colors flex items-center cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-calendar-check-line"></i>
                </div>
                {isNL ? 'Gratis Beoordeling Aanvragen' : 'Get Free Assessment'}
              </button>
              <button
                onClick={() => setShowPhoneModal(true)}
                className="bg-transparent border border-white text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-white/10 transition-colors flex items-center cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-phone-line"></i>
                </div>
                {isNL ? 'Bel +31 (0)6 1074 9063' : 'Call +31 (0)6 1074 9063'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Phone Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 relative">
            <button
              onClick={() => setShowPhoneModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-close-line"></i>
              </div>
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-phone-line ri-2x"></i>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {isNL ? 'Neem Contact Op met Thyamis' : 'Contact Thyamis'}
              </h3>

              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-2">1-800-THYAMIS</div>
                  <div className="text-sm text-gray-600">
                    {isNL ? 'Hoofdverkoopafdeling' : 'Main Sales Line'}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {isNL ? 'Alternatieve Contactmethoden' : 'Alternative Contact Methods'}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-phone-line"></i>
                      </div>
                      <span>+31 (0)6 1074 9063</span>
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center mr-2 text-primary">
                        <i className="ri-mail-line"></i>
                      </div>
                      <span>sales@thyamis.com</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {isNL ? 'Openingstijden' : 'Business Hours'}
                  </h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>{isNL ? 'Maandag - Vrijdag: 8:00 - 18:00 CET' : 'Monday - Friday: 8:00 AM - 6:00 PM CET'}</div>
                    <div>{isNL ? 'Zaterdag: 9:00 - 15:00 CET' : 'Saturday: 9:00 AM - 3:00 PM CET'}</div>
                    <div>{isNL ? 'Zondag: Gesloten' : 'Sunday: Closed'}</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <a
                  href="tel:+31061074906"
                  className="flex-1 bg-primary text-white px-4 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors text-center"
                >
                  {isNL ? 'Nu Bellen' : 'Call Now'}
                </a>
                <button
                  onClick={() => setShowPhoneModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg whitespace-nowrap font-medium hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {isNL ? 'Sluiten' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
